// ==============================|| REACT & THIRD-PARTY IMPORTS ||============================== //
import React, { useState, useMemo, useEffect } from 'react';
import {
    Drawer,
    Box,
    Stack,
    Typography,
    IconButton,
    InputBase,
    Menu,
    MenuItem,
    ListItemText,
    ListItemIcon,
    Divider,
    useTheme,
    Tooltip
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import {
    CloseOutlined,
    SearchOutlined,
    RightOutlined,
    DownOutlined,
    CloudUploadOutlined,
    EditOutlined
} from '@ant-design/icons';

// ==============================|| LOCAL COMPONENTS & ICONS ||============================== //
import {
    OrangeFolderIcon,
    AddNewFolderIcon,
    ModernIconExpandDown,
    ModernIconCollapseRight,
    CustomRenameIcon,
    CustomDeleteIcon,
    CustomMoveToIcon
} from './CommonFilemanagerIcon';
import { ModernIconMoreforfile } from 'utils/icons';

// ==============================|| TYPES IMPORTS ||============================== //
import { DirectoryNode, SidePanelDirectoryTreeViewProps, TabularFileItem } from 'types/CommonFileManager/commonFileManager';

// ==============================|| CONTEXT & HOOKS ||============================== //
import { useFileManagerState } from 'hooks/useFileManager';
import { getFolderLevel } from './CommonFileManager';





// ==============================|| STYLED COMPONENTS ||============================== //

const SearchWrapper = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '24px',
    padding: '6px 16px',
    width: '100%',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
        borderColor: theme.palette.grey[400]
    },
    '&:focus-within': {
        borderColor: theme.palette.primary.main,
        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
    }
}));

const SearchInputBase = styled(InputBase)(({ theme }) => ({
    marginLeft: 8,
    flex: 1,
    fontSize: '14px',
    '& input::placeholder': {
        color: theme.palette.text.disabled,
        opacity: 1
    }
}));

// Helper to determine if a node is an ancestor of the selected node
const isAncestorOfSelected = (node: DirectoryNode, selectedId?: string): boolean => {
    if (!selectedId) return false;
    if (node.id === selectedId) return false;
    if (!node.children) return false;
    const search = (children: DirectoryNode[]): boolean => {
        for (const child of children) {
            if (child.id === selectedId) return true;
            if (child.children && search(child.children)) return true;
        }
        return false;
    };
    return search(node.children);
};

const TreeItemRow = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'isLightSelected' && prop !== 'level'
})<{ isSelected: boolean; isLightSelected: boolean; level: number }>(({ theme, isSelected, isLightSelected, level }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: '2px 8px',
    paddingLeft: `${6 + level * 6}px`,
    borderRadius: '4px',
    cursor: 'pointer',
    position: 'relative',
    transition: 'background-color 0.15s ease',
    backgroundColor: isSelected ? theme.palette.action.selected : (isLightSelected ? theme.palette.grey[50] : 'transparent'),
    border: isLightSelected ? 'none' : '1px solid transparent',
    '&:hover': {
        backgroundColor: isSelected ? theme.palette.action.selected : theme.palette.action.hover,
        '& .more-options-btn': {
            opacity: 1
        }
    }
}));

export default function SidePanelDirectoryTreeView({
    open: propOpen,
    onClose: propOnClose,
    data: propData,
    selectedDirectoryId: propSelectedDirectoryId,
    onDirectorySelect: propOnDirectorySelect,
    onDirectoryDoubleClick: propOnDirectoryDoubleClick,
    onNewDirectoryClick: propOnNewDirectoryClick,
    onUploadClick: propOnUploadClick,
    onRenameClick: propOnRenameClick,
    onDeleteClick: propOnDeleteClick,
    onMoveToClick: propOnMoveToClick,
    container: propContainer
}: SidePanelDirectoryTreeViewProps) {
    const state = useFileManagerState();
    const { allowAdd, allowDelete, allowEdit, canManipulateItem, deletingItems = {} } = state;

    const open = propOpen ?? state.drawerOpen;
    const onClose = propOnClose ?? (() => state.setDrawerOpen(false));
    const data = propData ?? state.treeData ?? { id: 'files', name: 'Files', children: [] };
    const selectedDirectoryId = propSelectedDirectoryId ?? state.selectedDirectoryId;
    const container = propContainer ?? state.container;

    const onDirectorySelect = propOnDirectorySelect ?? state.handleDirectorySelect;
    const onDirectoryDoubleClick = propOnDirectoryDoubleClick ?? state.handleDirectoryDoubleClick;
    const onNewDirectoryClick = propOnNewDirectoryClick ?? state.handleNewDirectoryFromTree;
    const onUploadClick = propOnUploadClick ?? ((node: DirectoryNode) => {
        state.setUploadTargetDirectoryId(node.id);
        state.fileInputRef.current?.click();
    });
    const onRenameClick = propOnRenameClick ?? ((node: DirectoryNode) => {
        const item = {
            id: node.id,
            name: node.name,
            type: 'directory' as const,
            modifiedDate: '',
            size: '',
            modifiedBy: '',
            userType: state.directoryMap[node.id]?.userType
        };
        state.handleEditDirectory(item);
    });
    const onDeleteClick = propOnDeleteClick ?? ((node: DirectoryNode) => {
        state.handleDeleteDirectory({
            id: node.id,
            name: node.name,
            type: 'directory' as const,
            modifiedDate: '',
            size: '',
            modifiedBy: '',
            userType: state.directoryMap[node.id]?.userType
        });
    });
    const onMoveToClick = propOnMoveToClick ?? ((node: DirectoryNode) => {
        state.setMovingItemIds([node.id]);
        state.setIsMoveOpen(true);
    });

    const theme = useTheme();
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set(['files']));

    // Menu States
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [activeMenuNode, setActiveMenuNode] = useState<DirectoryNode | null>(null);

    // Close options menu on scroll or click outside
    useEffect(() => {
        if (Boolean(menuAnchorEl)) {
            const handleGlobalScroll = () => {
                handleMenuClose();
            };
            const handleGlobalClick = (e: MouseEvent) => {
                const target = e.target as HTMLElement;
                if (!target.closest('.more-options-menu') && !target.closest('.more-options-btn')) {
                    handleMenuClose();
                }
            };

            const timer = setTimeout(() => {
                window.addEventListener('scroll', handleGlobalScroll, true);
                document.addEventListener('click', handleGlobalClick, true);
            }, 0);

            return () => {
                clearTimeout(timer);
                window.removeEventListener('scroll', handleGlobalScroll, true);
                document.removeEventListener('click', handleGlobalClick, true);
            };
        }
    }, [menuAnchorEl]);

    const toggleExpand = (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const handleNodeClick = (node: DirectoryNode) => {
        if (onDirectorySelect) {
            onDirectorySelect(node);
        }
    };

    // Filter Tree Nodes based on Search Query
    const filteredTree = useMemo(() => {
        if (!searchQuery.trim()) return data;

        const query = searchQuery.toLowerCase().trim();

        // Recursive function to filter matching nodes and return matching node copy
        const filterNode = (node: DirectoryNode): DirectoryNode | null => {
            const childrenMatches: DirectoryNode[] = [];
            if (node.children) {
                node.children.forEach(child => {
                    const filteredChild = filterNode(child);
                    if (filteredChild) {
                        childrenMatches.push(filteredChild);
                    }
                });
            }

            const selfMatches = node.name.toLowerCase().includes(query);

            if (selfMatches || childrenMatches.length > 0) {
                // Auto-expand parents when matches exist
                setExpandedIds(prev => {
                    const next = new Set(prev);
                    next.add(node.id);
                    return next;
                });

                return {
                    ...node,
                    children: childrenMatches.length > 0 ? childrenMatches : undefined
                };
            }

            return null;
        };

        return filterNode(data) || { id: data.id, name: data.name };
    }, [data, searchQuery]);

    // Handle options menu click (3 vertical dots)
    const handleMenuOpen = (e: React.MouseEvent<HTMLButtonElement>, node: DirectoryNode) => {
        e.stopPropagation();
        if (menuAnchorEl && activeMenuNode?.id === node.id) {
            handleMenuClose();
        } else {
            setMenuAnchorEl(e.currentTarget);
            setActiveMenuNode(node);
        }
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setActiveMenuNode(null);
    };

    const handleNewDirectory = () => {
        if (onNewDirectoryClick && activeMenuNode) {
            onNewDirectoryClick(activeMenuNode);
        }
        handleMenuClose();
    };

    const handleUpload = () => {
        if (onUploadClick && activeMenuNode) {
            onUploadClick(activeMenuNode);
        }
        handleMenuClose();
    };

    const handleRename = () => {
        if (onRenameClick && activeMenuNode) {
            onRenameClick(activeMenuNode);
        }
        handleMenuClose();
    };

    const handleDelete = () => {
        if (onDeleteClick && activeMenuNode) {
            onDeleteClick(activeMenuNode);
        }
        handleMenuClose();
    };

    const handleMoveTo = () => {
        if (onMoveToClick && activeMenuNode) {
            onMoveToClick(activeMenuNode);
        }
        handleMenuClose();
    };

    const activeItem: TabularFileItem | null = activeMenuNode ? {
        id: activeMenuNode.id,
        name: activeMenuNode.name,
        type: 'directory',
        modifiedDate: '',
        size: '',
        modifiedBy: '',
        userType: state.directoryMap[activeMenuNode.id]?.userType
    } : null;

    // Render tree nodes recursively
    const renderTreeNodes = (node: DirectoryNode, level = 0) => {
        const hasChildren = node.children && node.children.length > 0;
        const isExpanded = expandedIds.has(node.id);
        const isMenuOpen = activeMenuNode !== null;
        const isSelected = isMenuOpen
            ? activeMenuNode.id === node.id
            : selectedDirectoryId === node.id;
        const isLightSelected = isMenuOpen
            ? (node.id === 'files' || node.id === selectedDirectoryId) && node.id !== activeMenuNode.id
            : node.id === 'files' && selectedDirectoryId !== 'files' && selectedDirectoryId !== undefined;

        const isDeleting = Object.keys(deletingItems).some(deletingId =>
            node.id === deletingId || node.id.startsWith(deletingId + '/')
        );

        const nodeItem: TabularFileItem = {
            id: node.id,
            name: node.name,
            type: 'directory',
            modifiedDate: '',
            size: '',
            modifiedBy: '',
            userType: state.directoryMap[node.id]?.userType
        };

        const hasAnyFolderAction = !isDeleting && (allowAdd ||
            (node.id !== data.id && (
                (allowEdit && canManipulateItem(nodeItem)) ||
                (allowDelete && canManipulateItem(nodeItem))
            )));

        return (
            <React.Fragment key={node.id}>
                <TreeItemRow
                    isSelected={isSelected}
                    isLightSelected={isLightSelected}
                    level={level}
                    onClick={() => {
                        if (isDeleting) return;
                        handleNodeClick(node);
                    }}
                    onDoubleClick={(e) => {
                        e.stopPropagation();
                        if (isDeleting) return;
                        if (onDirectoryDoubleClick) {
                            onDirectoryDoubleClick(node);
                        }
                    }}
                    sx={{
                        marginBottom: node.id === 'files' ? '12px' : '2px',
                        cursor: isDeleting ? 'default' : 'pointer'
                    }}
                >
                    {/* Expand/Collapse chevron */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 20,
                            height: 20,
                            mr: 0.5,
                            cursor: isDeleting ? 'default' : 'pointer',
                            color: theme.palette.text.disabled,
                            '&:hover': {
                                color: theme.palette.text.secondary
                            }
                        }}
                        onClick={hasChildren && !isDeleting ? (e) => toggleExpand(node.id, e) : undefined}
                    >
                        {hasChildren ? (
                            isExpanded ? (
                                <ModernIconExpandDown />
                            ) : (
                                <ModernIconCollapseRight />
                            )
                        ) : (
                            // Empty placeholder to keep alignment
                            <Box sx={{ width: 10 }} />
                        )}
                    </Box>

                    {/* Folder Icon */}
                    <Box sx={{ mr: 1, ml: 1, display: 'flex', alignItems: 'center' }}>
                        <OrangeFolderIcon />
                    </Box>

                    {/* Folder Name */}
                    <Typography
                        title={node.name}
                        variant="body1"
                        sx={{
                            fontSize: '15px',
                            fontWeight: isSelected ? 500 : 400,
                            color: theme.palette.text.primary,
                            flexGrow: 1,
                            userSelect: 'none',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            textDecoration: isDeleting ? 'line-through' : 'none'
                        }}
                    >
                        {node.name}
                    </Typography>

                    {/* Three Dots Menu Button (visible on hover) */}
                    {hasAnyFolderAction && (
                        <Tooltip title="More Actions" >
                            <IconButton
                                className="more-options-btn"
                                size="medium"
                                onClick={(e) => handleMenuOpen(e, node)}
                                sx={{
                                    opacity: isSelected ? 3 : 0,
                                    transition: 'opacity 0.15s ease',
                                    padding: '2px',
                                    color: theme.palette.text.disabled,
                                    '&:hover': {
                                        color: theme.palette.text.primary,
                                        backgroundColor: theme.palette.action.hover
                                    }
                                }}
                            >
                                <ModernIconMoreforfile style={{ fontSize: '10px' }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </TreeItemRow>

                {/* Render children if expanded */}
                {hasChildren && isExpanded && (
                    <Box>
                        {node.children!.map((child) => renderTreeNodes(child, level + 1))}
                    </Box>
                )}
            </React.Fragment>
        );
    };

    return (
        <Drawer
            variant="temporary"
            open={open}
            onClose={onClose}
            transitionDuration={{ enter: 450, exit: 400 }} // Slower transition duration for a premium, smooth sliding feel
            ModalProps={{
                keepMounted: true, // Ensures exit animation completes without snapping
                disablePortal: true, // Render inline in the DOM hierarchy to align coordinate transitions
                disableScrollLock: true, // Prevents body scroll locking layout shift 
                style: { position: 'absolute' } // Constrained context inside absolute parent container
            }}
            sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1201,
                '& .MuiDrawer-paper': {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    bottom: 0,
                    width: { xs: '100%', sm: 360 },
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: '4px 0 24px rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',

                    borderRight: `1px solid ${theme.palette.divider}`,
                    boxSizing: 'border-box'
                },
                '& .MuiBackdrop-root': {
                    position: 'absolute',
                    //backgroundColor: 'rgba(0, 0, 0, 0.45)',

                }
            }}
        >
            {/* Header */}
            <Box sx={{ p: 1, px: 2, pt: 1, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5" fontWeight={600} sx={{ fontSize: '18px', color: theme.palette.text.primary }}>
                    Files
                </Typography>
                <IconButton
                    onClick={onClose}
                    sx={{
                        color: theme.palette.primary.main,
                        '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.08)
                        }
                    }}
                >
                    <CloseOutlined style={{ fontSize: '16px' }} />
                </IconButton>
            </Box>

            {/* Search Input */}
            <Box sx={{ px: 1.5, pb: 1 }}>
                <SearchWrapper sx={{ borderRadius: '8px' }}>
                    <SearchOutlined style={{ color: theme.palette.text.disabled, fontSize: '16px' }} />
                    <SearchInputBase
                        sx={{ height: '24px' }}
                        placeholder="Search"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </SearchWrapper>
            </Box>

            <Divider sx={{ mx: 2, mb: 1 }} />

            {/* Tree Nodes List */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 1.5, pb: 2 }}>
                {renderTreeNodes(filteredTree)}
            </Box>

            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
                autoFocus={false}
                disableAutoFocusItem={true}
                disablePortal // Renders inline to scroll/move correctly with container scroll
                transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                sx={{ pointerEvents: 'none' }}
                slotProps={{
                    paper: {
                        className: 'more-options-menu',
                        sx: {
                            pointerEvents: 'auto',
                            borderRadius: '8px',
                            minWidth: 160,
                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
                            padding: '4px 0',
                            zIndex: 1300
                        }
                    }
                }}
            >
                {activeMenuNode && getFolderLevel(state.directoryMap, activeMenuNode.id) < 4 && allowAdd && (
                    <MenuItem onClick={handleNewDirectory} sx={{ py: 1, px: 2, '&:hover': { backgroundColor: theme.palette.action.hover } }}>
                        <ListItemIcon sx={{ minWidth: '30px !important', color: theme.palette.text.primary, fontWeight: 400 }}>
                            <AddNewFolderIcon />
                        </ListItemIcon>
                        <ListItemText
                            primary="New Folder"
                            primaryTypographyProps={{ sx: { fontSize: '14px', color: theme.palette.text.primary, fontWeight: 400 } }}
                        />
                    </MenuItem>
                )}
                {allowAdd && (
                    <MenuItem onClick={handleUpload} sx={{ py: 1, px: 2, '&:hover': { backgroundColor: theme.palette.action.hover } }}>
                        <ListItemIcon sx={{ minWidth: '30px !important', color: theme.palette.text.primary }}>
                            <CloudUploadOutlined style={{ fontSize: '20px' }} />
                        </ListItemIcon>
                        <ListItemText
                            primary="Upload files"
                            primaryTypographyProps={{ sx: { fontSize: '14px', color: theme.palette.text.primary, fontWeight: 400 } }}
                        />
                    </MenuItem>
                )}

                {activeMenuNode?.id !== data.id && [
                    activeItem && allowEdit && canManipulateItem(activeItem) && (
                        <MenuItem key="rename" onClick={handleRename} sx={{ py: 1, px: 2, '&:hover': { backgroundColor: theme.palette.action.hover } }}>
                            <ListItemIcon sx={{ minWidth: '30px !important', color: theme.palette.text.primary }}>
                                <EditOutlined style={{ fontSize: '18px' }} />
                            </ListItemIcon>
                            <ListItemText
                                primary="Rename"
                                primaryTypographyProps={{ sx: { fontSize: '14px', color: theme.palette.text.primary, fontWeight: 400 } }}
                            />
                        </MenuItem>
                    ),
                    activeItem && allowDelete && canManipulateItem(activeItem) && (
                        <MenuItem key="delete" onClick={handleDelete} sx={{ py: 1, px: 2, '&:hover': { backgroundColor: theme.palette.action.hover } }}>
                            <ListItemIcon sx={{ minWidth: '30px !important', color: theme.palette.text.primary }}>
                                <CustomDeleteIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary="Delete"
                                primaryTypographyProps={{ sx: { fontSize: '14px', color: theme.palette.text.primary, fontWeight: 400 } }}
                            />
                        </MenuItem>
                    ),
                    activeItem && allowEdit && canManipulateItem(activeItem) && (
                        <MenuItem key="moveto" onClick={handleMoveTo} sx={{ py: 1, px: 2, '&:hover': { backgroundColor: theme.palette.action.hover } }}>
                            <ListItemIcon sx={{ minWidth: '30px !important', color: theme.palette.text.primary }}>
                                <CustomMoveToIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary="Move to"
                                primaryTypographyProps={{ sx: { fontSize: '14px', color: theme.palette.text.primary, fontWeight: 300 } }}
                            />
                        </MenuItem>
                    )
                ].filter(Boolean)}
            </Menu>
        </Drawer>
    );
}
